import React, { useState } from 'react';
import { CarFront, CheckCircle2, FileWarning, HelpCircle, QrCode, Upload, UserCog, Database } from 'lucide-react';
import { OwnerButton, OwnerShell } from '../OwnerShell';
import { SelectField, TextAreaField } from '../OwnerFields';
import { UploadTile } from '../UploadTile';
import { StatusBadge } from '../../common/StatusBadge';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { ownerProfile, ownerVehicles } from '../../../data/ownerProfile';
import { complaintTone } from '../../../utils/ownerStatus';
import type { OwnerNav } from '../../../types/owner';
const cats = [{
  l: 'অ্যাকাউন্ট সমস্যা',
  i: UserCog
}, {
  l: 'গাড়ির তথ্য সমস্যা',
  i: CarFront
}, {
  l: 'ডকুমেন্ট আপলোড সমস্যা',
  i: Upload
}, {
  l: 'BRTA তথ্য অমিল',
  i: Database
}, {
  l: 'QR স্টিকার সমস্যা',
  i: QrCode
}, {
  l: 'অন্যান্য',
  i: HelpCircle
}];
export function OwnerSupport({
  nav
}: {
  nav: OwnerNav;
}) {
  const {
    complaints,
    submitComplaint
  } = useOwnerService();
  const mine = complaints.filter(c => c.ownerName === ownerProfile.name);
  const [cat, setCat] = useState('QR স্টিকার সমস্যা');
  const [desc, setDesc] = useState('');
  const [vehicle, setVehicle] = useState('প্রযোজ্য নয়');
  const [file, setFile] = useState(false);
  const [error, setError] = useState('');
  const [ticket, setTicket] = useState('');
  const submit = () => {
    if (desc.trim().length < 10) return setError('সমস্যার বিবরণ অন্তত ১০ অক্ষরে লিখুন');
    const id = submitComplaint({
      category: cat,
      desc,
      vehicle: vehicle === 'প্রযোজ্য নয়' ? '—' : vehicle,
      ownerName: ownerProfile.name,
      attachments: file ? 1 : 0
    });
    setTicket(id);
    setDesc('');
    setFile(false);
    setError('');
  };
  return <OwnerShell nav={nav} title="সহায়তা ও অভিযোগ" footer={!ticket ? <OwnerButton onClick={submit} data-id="element-2010" data-mp="1ni9lzlsl5nte">অভিযোগ জমা দিন</OwnerButton> : <OwnerButton variant="outline" onClick={() => setTicket('')} data-id="element-2011" data-mp="1ngopvrqmhwtf">নতুন অভিযোগ</OwnerButton>} data-id="element-2009" data-mp="zrw0yf8f4p8b">
      {ticket ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center" data-id="element-2012" data-mp="1sbcedqlmmr80">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" data-id="element-2013" data-mp="22349wzxghl8d" />
          <p className="mt-2 font-semibold text-navy-900" data-id="element-2014" data-mp="1x6x02w1xvpl5">অভিযোগ জমা হয়েছে</p>
          <p className="mx-auto mt-2 w-fit rounded-md bg-white px-3 py-1 font-mono text-base font-bold text-navy-900" data-id="element-2015" data-mp="1t3qza3xliqm2">{ticket}</p>
          <p className="mt-2 text-[12px] text-slate-600" data-id="element-2016" data-mp="1o5fkdesg1pwz">২ কর্মদিবসের মধ্যে একজন কর্মকর্তা আপনার সাথে যোগাযোগ করবেন।</p>
        </div> : <>
          <p className="mb-2 text-[13px] font-medium text-slate-700" data-id="element-2017" data-mp="1efnngdwvgkpc">সমস্যার ধরন নির্বাচন করুন</p>
          <div className="grid grid-cols-3 gap-2" role="radiogroup" data-id="element-2018" data-mp="19g1faivwhune">
            {cats.map(c => <button key={c.l} role="radio" aria-checked={cat === c.l} onClick={() => setCat(c.l)} className={`flex flex-col items-center gap-1.5 rounded-xl border p-2.5 text-center text-[11px] font-medium leading-tight ${cat === c.l ? 'border-navy-700 bg-navy-50 text-navy-900' : 'border-border bg-white text-slate-600'}`} data-id="element-2019" data-mp="15g90vpjtsua4">
                <c.i className="h-5 w-5" data-id="element-2020" data-mp="373viix7zdzca" />{c.l}
              </button>)}
          </div>
          <div className="mt-4 space-y-3.5" data-id="element-2021" data-mp="3c1x0tfxl1ump">
            <TextAreaField id="s-desc" label="সমস্যার বিবরণ" required value={desc} onChange={x => {
          setDesc(x);
          setError('');
        }} rows={4} placeholder="আপনার সমস্যাটি বিস্তারিত লিখুন…" error={error} data-id="element-2022" data-mp="3h2ufhijdw6jg" />
            <SelectField id="s-veh" label="গাড়ির নম্বর (ঐচ্ছিক)" value={vehicle} options={['প্রযোজ্য নয়', ...ownerVehicles.map(v => v.reg)]} onChange={setVehicle} data-id="element-2023" data-mp="2oe5j1wt9wpe1" />
            <UploadTile label="ছবি/ডকুমেন্ট" optional state={file ? 'uploaded' : 'missing'} onUpload={() => setFile(true)} data-id="element-2024" data-mp="2tavxygtcdexv" />
          </div>
        </>}

      <h2 className="mb-2 mt-5 flex items-center gap-1.5 text-sm font-semibold text-navy-900" data-id="element-2025" data-mp="326bzlk0c8ye0"><FileWarning className="h-4 w-4" data-id="element-2026" data-mp="25o7dbncy78dk" />আমার অভিযোগসমূহ</h2>
      <div className="space-y-2" data-id="element-2027" data-mp="25ll5rnq24svc">
        {mine.map(c => <div key={c.id} className="rounded-xl border border-border bg-white p-3" data-id="element-2028" data-mp="29ng7gtbwh89n">
            <div className="flex items-start justify-between gap-2" data-id="element-2029" data-mp="1qlfnmj96bgrj">
              <div className="min-w-0" data-id="element-2030" data-mp="1lljiggoqpjae">
                <p className="text-[13px] font-semibold text-navy-900" data-id="element-2031" data-mp="1lmupqqhe9969">{c.category}</p>
                <p className="truncate text-[11.5px] text-slate-500" data-id="element-2032" data-mp="20b8yfi1hrruq">{c.desc}</p>
                <p className="font-mono text-[10px] text-slate-400" data-id="element-2033" data-mp="1vj7h3hpgvaxu">{c.id} • {c.date}</p>
              </div>
              <StatusBadge tone={complaintTone(c.status)} className="!text-[10.5px]" data-id="element-2034" data-mp="299uglc31x4uz">{c.status}</StatusBadge>
            </div>
            {c.remarks.length > 0 && <p className="mt-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-[11.5px] text-slate-600" data-id="element-2035" data-mp="24e5cqwk011sv">ডিএমপি: {c.remarks[c.remarks.length - 1].text}</p>}
          </div>)}
        {mine.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white py-6 text-center text-xs text-slate-500" data-id="element-2036" data-mp="2e7w2e4rv3ia5">কোনো অভিযোগ নেই</p>}
      </div>
      <p className="mt-4 text-center text-[11px] text-slate-400" data-id="element-2037" data-mp="jwzdyz0den96">হেল্পলাইন: ০১৩২০-০৪২১১০ • রবি–বৃহস্পতি, সকাল ৯টা–বিকাল ৫টা</p>
    </OwnerShell>;
}